import React from 'react';
import moment from 'moment-timezone';
import { makeStyles } from '@mui/styles';
import Box from '@mui/material/Box';
import range from 'lodash/range';
import padStart from 'lodash/padStart';
import FormHelperText from '@mui/material/FormHelperText';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from 'components/ToggleButtonGroup';

const useStyles = makeStyles((theme) => ({
  paper: {
    boxShadow: theme.shadows[2],
  },
}));

const HOURS = range(12).map((i) => ({
  label: padStart(i + 1, 2, 0),
  id: i + 1,
}));

const MINUTES = range(60).map((i) => ({ label: padStart(i, 2, 0), id: i }));

const PERIOD = [
  { label: 'AM', id: 'AM' },
  { label: 'PM', id: 'PM' },
];

const Timepicker = ({
  label,
  className,
  hasError,
  helpText,
  onChange,
  value,
  size,
  margin,
  ...other
}) => {
  const classes = useStyles();

  if (global.IS_SERVER) return null;

  const date = moment().startOf('day');
  if (value >= 0) {
    date.set('minute', value);
  }

  const HOUR_OPTIONS = HOURS;

  const hourValue =
    value >= 0
      ? HOUR_OPTIONS.find((i) => i.id === Number(date.format('h')))
      : null;
  const minuteValue =
    value >= 0 ? MINUTES.find((i) => i.id === Number(date.format('m'))) : null;
  const amPmValue =
    value >= 0 ? PERIOD.find((i) => i.id === date.format('A')) : null;

  const onHourChange = (e, value) => {
    const start = moment().startOf('day');
    if (value) {
      if (value.id === '-1') {
        onChange('-1');
      } else {
        const m = date.format('m');
        const a = date.format('A');
        let nextH = value.id;
        if (a === 'PM' && nextH < 12) {
          nextH = nextH + 12;
        }
        onChange(
          start.clone().set('h', nextH).set('minute', m).diff(start, 'minutes'),
        );
      }
    } else {
      onChange();
    }
  };

  const onMinuteChange = (e, value) => {
    const start = moment().startOf('day');
    const neeDate = date.clone();
    neeDate.set('minute', value.id);
    onChange(neeDate.diff(start, 'minutes'));
  };

  const onAmPmChange = (e, value) => {
    if (!value) return;
    const start = moment().startOf('day');
    const h = Number(date.format('h'));
    let nextH = h;
    if (value === 'PM') {
      if (h < 12) {
        nextH = h + 12;
      }
    } else {
      if (h === 12) {
        nextH = 0;
      }
      if (h > 12) {
        nextH = h - 12;
      }
    }
    onChange(
      start
        .clone()
        .set('h', nextH)
        .set('minute', date.get('minute'))
        .diff(start, 'minutes'),
    );
  };

  return (
    <FormControl
      className={className}
      margin={margin}
      sx={{
        '&.MuiFormControl-fullWidth': {
          '& .MuiAutocomplete-root': {
            width: '100%',
            flex: 1,
          },
        },
      }}
      {...other}
    >
      {label && <FormLabel sx={{ mb: 1 }}>{label}</FormLabel>}
      <Box display="flex" alignItems="center">
        <Autocomplete
          size={size}
          disableClearable
          options={HOUR_OPTIONS}
          value={hourValue}
          onChange={onHourChange}
          renderInput={(params) => (
            <TextField
              sx={{
                '& .MuiInputBase-root': {
                  bgcolor: 'background.paper',
                },
              }}
              margin="none"
              placeholder="HH"
              {...params}
            />
          )}
          sx={{ mr: 1, width: 80 }}
          classes={{
            paper: classes.paper,
          }}
        />
        {value === '-1' ? null : (
          <React.Fragment>
            <Autocomplete
              size={size}
              disableClearable
              options={MINUTES}
              renderInput={(params) => (
                <TextField
                  sx={{
                    '& .MuiInputBase-root': {
                      bgcolor: 'background.paper',
                    },
                  }}
                  margin="none"
                  placeholder="MM"
                  {...params}
                />
              )}
              sx={{ mr: 1, width: 80 }}
              classes={{
                paper: classes.paper,
              }}
              value={minuteValue}
              onChange={onMinuteChange}
            />
            <ToggleButtonGroup
              color="primary"
              size={size}
              value={amPmValue?.id}
              exclusive
              onChange={onAmPmChange}
              sx={{
                '& .MuiToggleButtonGroup-grouped': {
                  borderRadius: '8px',
                },
              }}
            >
              <ToggleButton sx={{ px: 2, py: 0.3 }} value="AM">
                AM
              </ToggleButton>
              <ToggleButton sx={{ px: 2, py: 0.3 }} value="PM">
                PM
              </ToggleButton>
            </ToggleButtonGroup>
          </React.Fragment>
        )}
      </Box>
      {hasError && <FormHelperText>{helpText}</FormHelperText>}
    </FormControl>
  );
};

export const getMinutes = (data) => {
  if (!data) return '-1';
  const { h, m } = data;
  if (h < 0) return h;
  const d = moment.tz().startOf('day');
  return d.clone().set('h', h).set('minute', m).diff(d, 'minute');
};

export const getHourMinute = (minutes) => {
  if (minutes < 0) {
    return {
      h: 0,
      m: 0,
    };
  }
  const d = moment.tz().startOf('day').set('minute', minutes);
  return {
    h: d.format('H'),
    m: d.format('m'),
  };
};

export const formatMinutes = (minutes) => {
  if (minutes === undefined) return 0;
  return moment().startOf('day').set('minutes', minutes).format('hh:mm A');
};

export const minutesToUTCDate = (minutes) => {
  const timeData = getHourMinute(minutes);
  return moment
    .utc()
    .startOf('day')
    .hour(timeData.h)
    .minute(timeData.m)
    .toDate();
};

export const dateToMinutes = (date) => {
  if (date) {
    const cloned = date.clone().startOf('day');
    return date.diff(cloned, 'minutes');
  }
  return 0;
};

export default Timepicker;
